import React, { useEffect, useRef, useState } from 'react';
import { fetchLogo } from '../utils/logoCache';
import './splash.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api2b.pro';

/**
 * Splash overlay — checks server for loader_logo first.
 * If server has image → show only that logo (no dots).
 * If server has no image or fails → show dots fallback.
 * Minimum 2s display, then fades out.
 */
export default function Splash({ onSplashComplete }) {
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);
  // 'loading' = checking server, 'logo' = server image found, 'dots' = no server image
  const [mode, setMode] = useState('loading');
  const [logoUrl, setLogoUrl] = useState(null);
  const doneRef = useRef(false);
  const startTime = useRef(Date.now());

  // Fetch loader_logo via shared cache — no duplicate network request
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Uses the shared logoCache — same request serves App, Splash, Header, etc.
        const item = await fetchLogo();
        if (cancelled) return;
        const path = item?.loader_logo;

        if (cancelled) return;

        if (!path) {
          setMode('dots');
          return;
        }

        const fullUrl = path.startsWith('http')
          ? path
          : `${API_URL}/storage/${path}`;

        // Preload the image before showing
        const img = new window.Image();
        img.onload = () => {
          if (!cancelled) {
            setLogoUrl(fullUrl);
            setMode('logo');
          }
        };
        img.onerror = () => {
          if (!cancelled) setMode('dots');
        };
        img.src = fullUrl;
      } catch (err) {
        if (err.name !== 'AbortError' && !cancelled) {
          console.error('Splash: error fetching loader_logo', err);
          setMode('dots');
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Handle timing: min 2s display, safety 6s timeout
  useEffect(() => {
    if (mode === 'loading') {
      // Safety timeout: if server never responds, show dots then dismiss
      const safetyTimer = setTimeout(() => {
        setMode('dots');
      }, 4000);
      return () => clearTimeout(safetyTimer);
    }

    // Mode is 'logo' or 'dots' — enforce min 2s then fade out
    const elapsed = Date.now() - startTime.current;
    const remaining = Math.max(0, 2000 - elapsed);

    const timer = setTimeout(() => {
      if (!doneRef.current) triggerFadeOut();
    }, remaining);
    return () => clearTimeout(timer);
  }, [mode]);

  function triggerFadeOut() {
    setFading(true);
    setTimeout(() => {
      if (!doneRef.current) {
        doneRef.current = true;
        setVisible(false);
        onSplashComplete && onSplashComplete();
        setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
      }
    }, 500);
  }

  if (!visible) return null;

  return (
    <div
      className={`splash-screen${fading ? ' fade-out' : ''}`}
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      {mode === 'logo' && logoUrl ? (
        <img src={logoUrl} alt="splash" style={{ maxWidth: '80%', maxHeight: '80%' }} />
      ) : mode === 'dots' ? (
        <div className="three-dots-loading" aria-hidden="true">
          <span></span><span></span><span></span>
        </div>
      ) : null}
    </div>
  );
}
